import React, { useEffect, useRef } from "react";
import { connect } from "react-redux";
import { useNavigate, useLocation } from "react-router-dom";
import {
  fetchAffiliateReport,
  initializeAffiliateReportScreen,
  downloadAffiliateReport,
  setAffiliateReportFilters,
  clearAffiliateReportFilters,
  setAffiliateReportTableColumnFilters,
  clearAffiliateReportTableColumnFilters,
  saveAffiliateReportTableState,
  fetchAffiliateDependentLocationsDD,
  fetchAffiliateDependentNetworksDD,
  clearAffiliateDependentDropdowns,
} from "../../../redux/actions/Affiliate/affiliateReport";
import { fetchAffiliateDD } from "../../../redux/actions/Affiliate/affiliate";
import AffiliateReport from "../../../component/Affiliate/AffiliateReport";
import { loadingShow } from "../../../services/PostAPI";
import { ProgressBar } from "primereact/progressbar";
import { FILTER } from "../../../services/common/urlService";

function AffiliateReportScreen(props) {
  const {
    fetchAffiliateReport,
    initAffiliateReportScreen,
    showPageLevelLoader,
    affiliateReportListview,
    paginationInfo,
    screenPermissions,
    downloadAffiliateReport,
    isLoading,
    fetchAffiliateDD,
    fetchAffiliateDependentLocationsDD,
    fetchAffiliateDependentNetworksDD,
    clearAffiliateDependentDropdowns,
    affiliateDD,
    affiliateDependentLocationsDD,
    affiliateDependentNetworksDD,
    isLoadingAffiliateDependentDD,
    reportFilters,
    setAffiliateReportFilters,
    clearAffiliateReportFilters,
    tableColumnFilters,
    setAffiliateReportTableColumnFilters,
    clearAffiliateReportTableColumnFilters,
    savedTableState,
    saveAffiliateReportTableState,
  } = props;

  const navigate = useNavigate();
  const location = useLocation();
  const initialSavedState = useRef(savedTableState);

  const buildFetchParamsFromSavedState = (state) => {
    if (!state) return null;

    const filters = [];
    if (state.filters) {
      Object.keys(state.filters).forEach((key) => {
        if (
          key !== "global" &&
          state.filters[key]?.value !== null &&
          state.filters[key]?.value !== undefined
        ) {
          const filterValue = state.filters[key].value;
          filters.push({
            filterField: state.filters[key].filterKey || key,
            matchStrategy: state.filters[key].matchStrategy || FILTER.MATCH_STRATEGY.CONTAINS,
            query: filterValue,
          });
        }
      });
    }

    return {
      sortField: state.sortField || "id",
      sortOrder: state.sortOrder,
      pageNumber: state.page || 0,
      pageSize: state.rows || 10,
      filters: filters.length > 0 ? filters : undefined,
      userId: localStorage.getItem("userId"),
    };
  };

  const callAPIasync = async () => {
    await fetchAffiliateDD();
  };

  useEffect(() => {
    callAPIasync();
    
    const isFromDetail = location.state?.fromDetail;
    const hasExistingFilters = reportFilters && (
      reportFilters.startDate ||
      reportFilters.endDate ||
      reportFilters.locationId?.length > 0 ||
      reportFilters.networkId?.length > 0 ||
      reportFilters.affiliateId?.length > 0
    );
    
    if (isFromDetail) {
      // Coming back from detail page - preserve filters
      const savedParams = buildFetchParamsFromSavedState(initialSavedState.current);
      
      if (savedParams && hasExistingFilters) {
        // Combine saved table state with report filters
        fetchAffiliateReport({
          ...savedParams,
          startDate: reportFilters.startDate,
          endDate: reportFilters.endDate,
          locationId: reportFilters.locationId,
          networkId: reportFilters.networkId,
          affiliateId: reportFilters.affiliateId,
        });
      } else if (savedParams) {
        fetchAffiliateReport(savedParams);
      }
      navigate(location.pathname, { replace: true, state: {} });
    } else {
      // Fresh load - check for saved filters or table state
      if (hasExistingFilters) {
        // Restore data with existing report filters
        const savedParams = buildFetchParamsFromSavedState(initialSavedState.current);
        fetchAffiliateReport({
          pageNumber: savedParams?.pageNumber || 0,
          pageSize: savedParams?.pageSize || 10,
          sortField: savedParams?.sortField || "id",
          sortOrder: savedParams?.sortOrder || -1,
          userId: localStorage.getItem("userId"),
          startDate: reportFilters.startDate,
          endDate: reportFilters.endDate,
          locationId: reportFilters.locationId,
          networkId: reportFilters.networkId,
          affiliateId: reportFilters.affiliateId,
        });
      } else {
        const savedParams = buildFetchParamsFromSavedState(initialSavedState.current);
        
        if (savedParams) {
          // Restore from saved table state
          fetchAffiliateReport(savedParams);
        } else {
          // Initialize fresh
          initAffiliateReportScreen();
          clearAffiliateReportFilters();
          clearAffiliateReportTableColumnFilters();
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadLazyData = (props) => {
    let filters = [];
    
    if (Array.isArray(props.filters)) {
      filters = props.filters;
    } else if (props.filters && typeof props.filters === 'object') {
      Object.keys(props.filters).forEach((key) => {
        if (
          key !== "global" &&
          props.filters[key]?.value !== null &&
          props.filters[key]?.value !== undefined &&
          props.filters[key]?.value !== ""
        ) {
          const filterValue = props.filters[key].value;
          filters.push({
            filterField: props.filters[key].filterKey || key,
            matchStrategy: props.filters[key].matchStrategy || FILTER.MATCH_STRATEGY.CONTAINS,
            query: filterValue,
          });
        }
      });
    }

    let newProps = {
      sortField: props.sortField,
      sortOrder: props.sortOrder,
      pageNumber: props.page,
      pageSize: props.rows,
      userId: localStorage.getItem("userId"),
      filters: filters.length > 0 ? filters : undefined,
      additinalFilter: props.additinalFilter ?? reportFilters,
    };

    fetchAffiliateReport(newProps);
  };

  const handleTableStateChange = (tableState) => {
    saveAffiliateReportTableState(tableState);
  };

  const affiliateReportProps = {
    downloadAffiliateReport,
    isLoading,
    screenPermissions,
    fetchAffiliateReport: loadLazyData,
    tableData: {
      columns: affiliateReportListview?.columns,
      rows: affiliateReportListview?.rows,
    },
    paginationInfo: {
      pageSize: paginationInfo?.pageSize,
      pageNumber: paginationInfo?.pageNumber,
      totalPages: paginationInfo?.totalPages,
      totalRecords: paginationInfo?.totalRecords,
    },
    affiliateDD,
    affiliateDependentLocationsDD,
    affiliateDependentNetworksDD,
    isLoadingAffiliateDependentDD,
    fetchAffiliateDependentLocationsDD,
    fetchAffiliateDependentNetworksDD,
    clearAffiliateDependentDropdowns,
    reportFilters,
    setReportFilters: setAffiliateReportFilters,
    clearReportFilters: clearAffiliateReportFilters,
    tableColumnFilters,
    setTableColumnFilters: setAffiliateReportTableColumnFilters,
    clearTableColumnFilters: clearAffiliateReportTableColumnFilters,
    savedTableState,
    onTableStateChange: handleTableStateChange,
    affiliateReportSummary: props.affiliateReportSummary,
  };

  if (showPageLevelLoader) {
    loadingShow("block");
    return null;
  }
  loadingShow("none");

  return (
    <div>
      {isLoading && (
        <ProgressBar mode="indeterminate" style={{ height: "6px" }} />
      )}
      <AffiliateReport {...affiliateReportProps} />
    </div>
  );
}

const mapDispatchToProps = (dispatch) => {
  return {
    initAffiliateReportScreen: () => dispatch(initializeAffiliateReportScreen()),
    fetchAffiliateReport: (payload) => dispatch(fetchAffiliateReport(payload)),
    downloadAffiliateReport: (payload) => dispatch(downloadAffiliateReport(payload)),
    setAffiliateReportFilters: (payload) => dispatch(setAffiliateReportFilters(payload)),
    clearAffiliateReportFilters: () => dispatch(clearAffiliateReportFilters()),
    setAffiliateReportTableColumnFilters: (payload) =>
      dispatch(setAffiliateReportTableColumnFilters(payload)),
    clearAffiliateReportTableColumnFilters: () =>
      dispatch(clearAffiliateReportTableColumnFilters()),
    saveAffiliateReportTableState: (payload) =>
      dispatch(saveAffiliateReportTableState(payload)),
    fetchAffiliateDD: (payload) => dispatch(fetchAffiliateDD(payload)),
    fetchAffiliateDependentLocationsDD: (affiliateIds) => 
      dispatch(fetchAffiliateDependentLocationsDD(affiliateIds)),
    fetchAffiliateDependentNetworksDD: (affiliateIds) => 
      dispatch(fetchAffiliateDependentNetworksDD(affiliateIds)),
    clearAffiliateDependentDropdowns: () => dispatch(clearAffiliateDependentDropdowns()),
  };
};

const mapStateToProps = (state, ownProps) => {
  return {
    ...ownProps,
    affiliateReportListview: selectAffiliateReportList(state.affiliateReportReducer),
    showPageLevelLoader: selectIsLoadingPage(state.affiliateReportReducer),
    paginationInfo: selectPaginationInfo(state.affiliateReportReducer),
    isPageLevelError: selectIsPageLevelError(state.affiliateReportReducer),
    isLoading: selectIsLoading(state.affiliateReportReducer),
    reportFilters: state.affiliateReportReducer?.reportFilters,
    tableColumnFilters: state.affiliateReportReducer?.tableColumnFilters,
    savedTableState: state.affiliateReportReducer?.savedTableState,
    affiliateDD: selectFromAffiliateReducer(state, "affiliateDD"),
    affiliateDependentLocationsDD: state.affiliateReportReducer?.affiliateDependentLocationsDD || [],
    affiliateDependentNetworksDD: state.affiliateReportReducer?.affiliateDependentNetworksDD || [],
    isLoadingAffiliateDependentDD: state.affiliateReportReducer?.isLoadingAffiliateDependentDD,
    affiliateReportSummary: state.affiliateReportReducer?.affiliateReportSummary,
  };
};

const selectIsLoading = (reducer) => reducer?.isLoading;
const selectPaginationInfo = (reducer) => reducer?.paginationInfo;
const selectIsLoadingPage = (reducer) => reducer?.isLoadingPage;
const selectIsPageLevelError = (reducer) => reducer?.isPageLevelError;
const selectAffiliateReportList = (reducer) => reducer?.affiliateReportListview;

const selectFromLocationReducer = (state, path) => {
  return state.locationReducer?.[path];
};

const selectFromNetworkReducer = (state, path) => {
  return state.rateadminNetworksReducer?.[path];
};

const selectFromAffiliateReducer = (state, path) => {
  return state.affiliateReducer?.[path];
};

export default connect(mapStateToProps, mapDispatchToProps)(AffiliateReportScreen);
